import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import noop from 'lodash/noop';
import omit from 'lodash/omit';
import isFunction from 'lodash/isFunction';
import map from 'lodash/map';
import isEmpty from 'lodash/isEmpty';

import { RadioGroup, RadioGroupItem } from '@ps-design-system/components/RadioGroup';

import { getMethodMetaById, NEW_CARD, useStripeElements } from './utils';
import PaymentElement from './PaymentElement';

const PaymentSelector = ({
  cards = [],
  selectedCard: selectedCardExternal = null,
  setSelectedCard: setSelectedCardExternal = noop,
  cardHoldersInputProps = {},
  error = ''
}) => {
  const [showPaymentElement, setShowPaymentElement] = useState(false);
  const [selectedCard, setSelectedCard] = useState(selectedCardExternal);

  const { availableWallets, selectedWallet, setSelectedWallet, isInitialized } = useStripeElements();

  // Set selected card default value
  useEffect(() => {
    if (selectedCard) {
      return;
    }
    const firstCard = get(cards, '[0]');
    setSelectedCard(firstCard || NEW_CARD);
  }, [cards]);

  // Toggle stripe payment element visibility
  useEffect(() => {
    setShowPaymentElement('new_card' === selectedCard?.id);
  }, [selectedCard]);

  const handleCardChange = useCallback((card) => {
    setSelectedCard(card);
    if (isFunction(setSelectedCardExternal)) {
      const originalCard = omit(card, ['label', 'value']);
      setSelectedCardExternal('new_card' === card.id ? null : originalCard);
    }
  }, [selectedCard, setSelectedCardExternal]);


  if (!isInitialized) {
    return null;
  }

  if (isEmpty(availableWallets)) {
    return <PaymentElement
      cardHoldersInputProps={cardHoldersInputProps}
      selectedCard={selectedCard}
      error={error}
      onCardChange={handleCardChange}
      showStripeElement={showPaymentElement}
      cards={cards}
    />;
  }

  return (
    <div className="flex column tw-gap-16">
      <RadioGroup
        className="payment-radio"
        defaultValue="card"
        variant="box"
        value={selectedWallet}
        onValueChange={setSelectedWallet}
      >
        <RadioGroupItem
          className="full-width"
          value="card"
          id="card"
          label="Credit Card"
          data-testing-id="credit-card-option"
          allowEventBubbling
          note={
            <div className="tw-pt-8 element" style={{ fontSize: 16 }}>
              <PaymentElement
                cardHoldersInputProps={cardHoldersInputProps}
                selectedCard={selectedCard}
                error={error}
                onCardChange={handleCardChange}
                showStripeElement={showPaymentElement}
                cards={cards}
              />
            </div>
          }
        />

        {map(availableWallets, (wallet) => {
          const meta = getMethodMetaById(wallet);
          if (!meta) {
            return null;
          }
          const { label, image, testingId } = meta;
          return (
            <RadioGroupItem
              value={wallet}
              id={wallet}
              data-testing-id={testingId}
              label={
                <div className="tw-flex tw-justify-start tw-gap-8">
                  <div style={{ width: 46 }}>
                    <img style={{ height: 24 }} src={image} alt={label} />
                  </div>
                  <div>{label}</div>
                </div>
              }
            />
          );
        })}
      </RadioGroup>
    </div>
  );
};

PaymentSelector.propTypes = {
  amount: PropTypes.number,
  currency: PropTypes.string,
  onElementsMounted: PropTypes.func,
  cards: PropTypes.array,
  setSelectedCard: PropTypes.func,
  selectedCard: PropTypes.object,
  cardHoldersInputProps: PropTypes.object,
  error: PropTypes.string
};

export default PaymentSelector;
